import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE, formatCurrency } from '../config/api';
import { money } from '../billPayment';
import './FlavorChangeModal.css';

// Keep in step with GiftBoxPage.js and BackEndWeb2/src/flavorChange.js.
const WEIGHT_EXTRA = 10000;
const EGG_EXTRA = 7000;

const parseItems = (raw) => {
    try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        return [];
    }
};

const slotsOf = (line) => (Array.isArray(line.items) && line.items.length > 0 ? line.items : [line]);
const isSingleLine = (line) => Boolean(line.isSingle) || /^bánh lẻ:/i.test(String(line.name || ''));
const sameName = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

function eggOf(slot, line) {
    const source = [slot, line].find((x) => x && (x.hasEgg !== undefined || x.egg !== undefined));
    if (!source) return false;
    if (source.hasEgg !== undefined) return Boolean(source.hasEgg);
    const egg = String(source.egg).toLowerCase();
    return egg.includes('1') && !egg.includes('không');
}

const weightOf = (slot, line) => String(slot.weight || line.weight || '150g');
const extraOf = (weight, hasEgg) => (weight.includes('180') ? WEIGHT_EXTRA : 0) + (hasEgg ? EGG_EXTRA : 0);
const kindOf = (p) => (String(p.category || '').toLowerCase().includes('dẻo') ? 'Bánh dẻo' : 'Bánh nướng');
const signed = (n) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${formatCurrency(Math.abs(n))}`;

/** Message the shop can paste into Zalo after a change that moves money. */
export function customerMessage(bill, result, sepayConfig) {
    const lines = result.changes.map((c) => `• Ô ${c.slot + 1}: ${c.from.name} → ${c.to.name}`).join('\n');
    const head = `Cookie Labs xin chào ${bill.customerName || 'anh/chị'}! Đơn #${bill.orderCode} đã được đổi vị theo yêu cầu:\n${lines}\nTổng mới: ${formatCurrency(result.newTotal)}.`;
    if (result.amountDue > 0) {
        const bank = sepayConfig?.bankShortName || 'MB';
        const acc = sepayConfig?.accountNumber || '';
        return `${head}\nAnh/chị đã thanh toán ${formatCurrency(result.paidBefore)}, vui lòng chuyển thêm ${formatCurrency(result.amountDue)}`
            + ` — ${bank} ${acc}, nội dung: ${bill.orderCode}.\nHoặc thanh toán nhanh tại: https://order.cookielabs.vn/my-orders?query=${encodeURIComponent(bill.orderCode)}`;
    }
    if (result.refundDue > 0) {
        return `${head}\nTiệm sẽ hoàn lại anh/chị ${formatCurrency(result.refundDue)} tiền chênh lệch. Anh/chị gửi giúp tiệm số tài khoản nhận tiền nhé!`;
    }
    return head;
}

export default function FlavorChangeModal({ bill, products, onClose, onApplied, onBillRefreshed }) {
    const lines = useMemo(() => parseItems(bill.items), [bill.items]);
    const [picks, setPicks] = useState({});
    const [preview, setPreview] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    // Kept apart: resetting the picks after a conflict must not wipe the message explaining it.
    const [previewError, setPreviewError] = useState('');
    const [applyError, setApplyError] = useState('');
    const [note, setNote] = useState('');
    const [applying, setApplying] = useState(false);
    const requestSeq = useRef(0);

    const cakes = useMemo(() => {
        const list = (products || []).filter((p) => p && p.name && !String(p.name).includes('Hộp'));
        const byName = (a, b) => String(a.name).localeCompare(String(b.name), 'vi');
        return {
            'Bánh nướng': list.filter((p) => kindOf(p) === 'Bánh nướng').sort(byName),
            'Bánh dẻo': list.filter((p) => kindOf(p) === 'Bánh dẻo').sort(byName),
        };
    }, [products]);

    const swaps = useMemo(() => Object.entries(picks).map(([key, productId]) => {
        const [line, slot] = key.split(':').map(Number);
        return { line, slot, productId };
    }), [picks]);

    // The server is the authority on prices: every change is re-quoted from there.
    useEffect(() => {
        if (swaps.length === 0) {
            setPreview(null);
            setPreviewError('');
            setPreviewing(false);
            return undefined;
        }
        const seq = ++requestSeq.current;
        setPreviewing(true);
        const timer = setTimeout(async () => {
            try {
                const res = await axios.post(`${API_BASE}/bill/changeFlavor/${bill.id}/preview`, { swaps });
                if (seq !== requestSeq.current) return;
                setPreview(res.data);
                setPreviewError('');
            } catch (err) {
                if (seq !== requestSeq.current) return;
                setPreview(null);
                setPreviewError(err.response?.data?.message || 'Không tính được chênh lệch, vui lòng thử lại.');
            } finally {
                if (seq === requestSeq.current) setPreviewing(false);
            }
        }, 250);
        return () => clearTimeout(timer);
    }, [swaps, bill.id]);

    const pick = (lineIdx, slotIdx, productId) => {
        setApplyError('');
        setPicks((prev) => {
            const next = { ...prev };
            const key = `${lineIdx}:${slotIdx}`;
            if (productId) next[key] = productId;
            else delete next[key];
            return next;
        });
    };

    const apply = async () => {
        if (!preview || previewing || applying) return;
        setApplying(true);
        setApplyError('');
        try {
            const res = await axios.post(`${API_BASE}/bill/changeFlavor/${bill.id}`, {
                swaps,
                version: preview.version,
                note,
            });
            onApplied(res.data.bill, res.data);
        } catch (err) {
            const status = err.response?.status;
            setApplyError(err.response?.data?.message || 'Không lưu được thay đổi. Đơn hàng chưa bị đổi.');
            if (status === 409) {
                // Someone else changed the order: show them the fresh one and start over.
                try {
                    const fresh = await axios.get(`${API_BASE}/bill/version/${bill.id}`);
                    if (fresh.data?.bill && onBillRefreshed) onBillRefreshed(fresh.data.bill);
                } catch (e) { /* the error above already tells them to retry */ }
                setPicks({});
            }
        } finally {
            setApplying(false);
        }
    };

    const isBaking = String(bill.status || '').toLowerCase().includes('làm');

    return (
        <div className="modal-overlay" onClick={applying ? undefined : onClose}>
            <div className="modal-card fc-card" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="fc-title">
                <div className="fc-head">
                    <div>
                        <div className="fc-eyebrow">Đơn #{bill.orderCode} · {bill.customerName}</div>
                        <h4 id="fc-title" className="fc-title">Đổi vị bánh</h4>
                    </div>
                    <button type="button" className="fc-close" onClick={onClose} disabled={applying} aria-label="Đóng">
                        <i className="fa-solid fa-xmark" />
                    </button>
                </div>

                {isBaking && (
                    <div className="fc-banner warn">
                        <i className="fa-solid fa-fire-burner" />
                        <span>Đơn đang <strong>làm bánh</strong> — báo bếp trước khi đổi để không làm nhầm.</span>
                    </div>
                )}

                <p className="fc-hint">
                    Chọn bánh mới cho ô cần đổi. Trọng lượng và trứng muối của từng ô được giữ nguyên; giá tính lại theo menu hiện tại.
                </p>

                <div className="fc-lines">
                    {lines.map((line, lineIdx) => {
                        const slots = slotsOf(line);
                        const qty = Math.max(1, Number(line.qty) || 1);
                        return (
                            <section key={line.id || lineIdx} className="fc-line">
                                <div className="fc-line-head">
                                    <span className="fc-line-name">{qty}× {isSingleLine(line) ? 'Bánh lẻ' : line.name}</span>
                                    {qty > 1 && <span className="fc-line-qty">Áp dụng cho cả {qty} {isSingleLine(line) ? 'bánh' : 'hộp'}</span>}
                                </div>

                                {slots.map((slot, slotIdx) => {
                                    const key = `${lineIdx}:${slotIdx}`;
                                    const weight = weightOf(slot, line);
                                    const hasEgg = eggOf(slot, line);
                                    const extra = extraOf(weight, hasEgg);
                                    const oldPrice = Number(slot.price) > 0 ? money(slot.price) : null;
                                    const chosenId = picks[key] || '';
                                    const quoted = preview?.changes?.find((c) => c.line === lineIdx && c.slot === slotIdx);

                                    return (
                                        <div key={slot.uid || slotIdx} className={`fc-slot ${chosenId ? 'on' : ''}`}>
                                            <span className="fc-slot-num">{slotIdx + 1}</span>
                                            <div className="fc-slot-now">
                                                <div className="fc-slot-name" title={slot.name}>{slot.name}</div>
                                                <div className="fc-chips">
                                                    <span className="fc-chip">{weight}</span>
                                                    <span className={`fc-chip ${hasEgg ? 'egg' : ''}`}>{hasEgg ? '1 trứng' : 'Không trứng'}</span>
                                                    {oldPrice !== null && <span className="fc-chip price">{formatCurrency(oldPrice)}</span>}
                                                </div>
                                            </div>
                                            <i className="fa-solid fa-arrow-right fc-slot-arrow" aria-hidden="true" />
                                            <div className="fc-slot-pick">
                                                <select
                                                    value={chosenId}
                                                    onChange={(e) => pick(lineIdx, slotIdx, e.target.value)}
                                                    aria-label={`Đổi bánh ô ${slotIdx + 1} (${slot.name})`}
                                                    disabled={applying}
                                                >
                                                    <option value="">Giữ nguyên</option>
                                                    {Object.entries(cakes).map(([kind, list]) => list.length > 0 && (
                                                        <optgroup key={kind} label={kind}>
                                                            {list.map((p) => {
                                                                const price = money(p.price) + extra;
                                                                const delta = oldPrice !== null ? price - oldPrice : null;
                                                                const current = sameName(p.name, slot.name);
                                                                return (
                                                                    <option key={p.id} value={p.id} disabled={current}>
                                                                        {p.name} · {formatCurrency(price)}
                                                                        {current ? ' (đang chọn)' : delta ? ` (${signed(delta)})` : ''}
                                                                        {p.stock_status === 'soldout' ? ' · hết hàng' : ''}
                                                                    </option>
                                                                );
                                                            })}
                                                        </optgroup>
                                                    ))}
                                                </select>
                                                {quoted && (
                                                    <span className={`fc-delta ${quoted.delta > 0 ? 'up' : quoted.delta < 0 ? 'down' : ''}`}>
                                                        {quoted.delta === 0 ? 'Cùng giá' : `${signed(quoted.delta)}${qty > 1 ? ` × ${qty}` : ''}`}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </section>
                        );
                    })}
                </div>

                <div className="fc-summary" aria-live="polite">
                    {swaps.length === 0 ? (
                        <div className="fc-empty">Chưa đổi ô nào.</div>
                    ) : previewing && !preview ? (
                        <div className="fc-empty"><i className="fa-solid fa-spinner fa-spin" /> Đang tính chênh lệch…</div>
                    ) : preview ? (
                        <>
                            <div className="fc-totals">
                                <div><span>Tổng hiện tại</span><strong>{formatCurrency(preview.oldTotal)}</strong></div>
                                <div><span>Tổng sau đổi</span><strong>{formatCurrency(preview.newTotal)}</strong></div>
                                <div className={`fc-diff ${preview.difference > 0 ? 'up' : preview.difference < 0 ? 'down' : ''}`}>
                                    <span>Chênh lệch</span><strong>{preview.difference === 0 ? '0 ₫' : signed(preview.difference)}</strong>
                                </div>
                            </div>
                            <PaymentOutcome preview={preview} />
                        </>
                    ) : null}
                    {[previewError, applyError].filter(Boolean).map((msg) => (
                        <div key={msg} className="fc-banner error"><i className="fa-solid fa-circle-exclamation" /><span>{msg}</span></div>
                    ))}
                </div>

                <label className="fc-note">
                    <span>Ghi chú (lưu vào lịch sử đơn)</span>
                    <input
                        type="text"
                        value={note}
                        maxLength={500}
                        placeholder="VD: Khách gọi đổi lúc 14h"
                        onChange={(e) => setNote(e.target.value)}
                        disabled={applying}
                    />
                </label>

                <div className="fc-actions">
                    <button type="button" className="btn-outline-paper" onClick={onClose} disabled={applying}>Huỷ</button>
                    <button
                        type="button"
                        className="btn-lacquer"
                        onClick={apply}
                        disabled={!preview || previewing || applying || Boolean(previewError)}
                    >
                        {applying ? <><i className="fa-solid fa-spinner fa-spin" /> Đang lưu…</> : 'Xác nhận đổi vị'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function PaymentOutcome({ preview }) {
    if (!preview.receivedMoney) {
        return (
            <div className="fc-outcome neutral">
                <i className="fa-regular fa-clock" />
                <span>Khách <strong>chưa thanh toán</strong> — sẽ trả theo tổng mới <strong>{formatCurrency(preview.newTotal)}</strong>.</span>
            </div>
        );
    }
    if (preview.amountDue > 0) {
        return (
            <div className="fc-outcome due">
                <i className="fa-solid fa-hand-holding-dollar" />
                <span>
                    Khách đã trả <strong>{formatCurrency(preview.paidBefore)}</strong> → cần thu thêm <strong>{formatCurrency(preview.amountDue)}</strong>.
                    {' '}Đơn chuyển sang <em>{preview.status}</em>; khách quét QR phần chênh lệch trong “Đơn của tôi”, hoặc bạn bấm <strong>Đã thu</strong> khi nhận tiền mặt.
                </span>
            </div>
        );
    }
    if (preview.refundDue > 0) {
        return (
            <div className="fc-outcome refund">
                <i className="fa-solid fa-rotate-left" />
                <span>
                    Khách đã trả <strong>{formatCurrency(preview.paidBefore)}</strong> → cần <strong>hoàn lại {formatCurrency(preview.refundDue)}</strong>.
                    {' '}Bấm <strong>Đã hoàn</strong> trong chi tiết đơn sau khi chuyển trả.
                </span>
            </div>
        );
    }
    return (
        <div className="fc-outcome ok">
            <i className="fa-solid fa-circle-check" />
            <span>Không phát sinh chênh lệch tiền.</span>
        </div>
    );
}
